import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ShoppingCart,
  ChevronDown,
  ChevronRight,
  ArrowLeft,
  Check,
  MapPin,
  Package,
  Filter
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { useAuth } from '@/context/AuthContext';
import { usePurchaseOrders } from '../hooks/usePurchaseOrders';
import { IngredientPreloadService, type LocationIngredientGroup } from '@/services/ingredientPreloadService';
import { authFetch } from '@/services/api.service';
import type { PurchaseOrderFormData, PurchaseOrderItem, PreloadedIngredient } from '@/types/purchase-order.types';
import { PurchaseOrderIngredientsTable } from './PurchaseOrderIngredientsTable';
import { toast } from 'sonner';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3003/api';

interface DatabaseIngredient {
  id: number;
  name: string;
  description?: string;
  costUnit: string;
  supplierId: number;
  unit: string;
  category: string;
  supplierName: string;
  stockActual: string;
  stockMinimo: number;
  consumo: number;
}

interface DatabaseLocation {
  id: number;
  nombre: string;
  direccion?: string;
  telefono?: string;
}

interface DatabaseSupplier {
  id: string;
  businessName: string;
  isActive: boolean;
}

export const PurchaseOrderCreatePage: React.FC = () => {
  const navigate = useNavigate();
  const { selectedLocation } = useAuth();
  const { createOrder, createOrdersByLocations, loading: creatingOrder } = usePurchaseOrders(selectedLocation?.id || 'loc-1');

  // State for database data
  const [ingredients, setIngredients] = useState<DatabaseIngredient[]>([]);
  const [suppliers, setSuppliers] = useState<DatabaseSupplier[]>([]);
  const [accessibleLocations, setAccessibleLocations] = useState<DatabaseLocation[]>([]);
  const [ingredientsLoading, setIngredientsLoading] = useState(true);
  const [suppliersLoading, setSuppliersLoading] = useState(true);
  const [locationsLoading, setLocationsLoading] = useState(true);

  const [locationGroups, setLocationGroups] = useState<LocationIngredientGroup[]>([]);
  const [selectedSupplier, setSelectedSupplier] = useState<DatabaseSupplier | null>(null);
  const [selectedLocationId, setSelectedLocationId] = useState<string>(selectedLocation?.id || '2'); // Usar ID 2 como defecto (Surco)
  const [notes, setNotes] = useState('');
  const [expectedDeliveryDate, setExpectedDeliveryDate] = useState('');
  const [selectedIngredientFilter, setSelectedIngredientFilter] = useState<string>('all');

  // Cargar ingredientes desde la base de datos
  useEffect(() => {
    const loadIngredients = async () => {
      try {
        setIngredientsLoading(true);
        const response = await authFetch(`${API_BASE_URL}/purchase-orders/ingredients`);

        if (!response.ok) {
          throw new Error('Error al cargar ingredientes');
        }

        const data = await response.json();
        if (data.success) {
          setIngredients(data.data || []);
        } else {
          toast.error('Error al cargar ingredientes');
        }
      } catch (error) {
        console.error('Error loading ingredients:', error);
        toast.error('Error al cargar ingredientes');
      } finally {
        setIngredientsLoading(false);
      }
    };

    loadIngredients();
  }, []);

  // Cargar ubicaciones accesibles
  useEffect(() => {
    const loadLocations = async () => {
      try {
        setLocationsLoading(true);
        const response = await authFetch(`${API_BASE_URL}/purchase-orders/accessible-locations`);

        if (!response.ok) {
          throw new Error('Error al cargar ubicaciones');
        }

        const data = await response.json();
        if (data.success) {
          setAccessibleLocations(data.data);
        } else {
          toast.error('Error al cargar ubicaciones');
        }
      } catch (error) {
        console.error('Error loading locations:', error);
        toast.error('Error al cargar ubicaciones');
      } finally {
        setLocationsLoading(false);
      }
    };

    loadLocations();
  }, []);

  // Cargar proveedores
  useEffect(() => {
    const loadSuppliers = async () => {
      try {
        setSuppliersLoading(true);
        const locationId = selectedLocation?.id || '2'; // Default to Surco
        const response = await authFetch(`${API_BASE_URL}/locations/${locationId}/purchase-orders/suppliers/list`);

        if (!response.ok) {
          throw new Error('Error al cargar proveedores');
        }

        const data = await response.json();
        if (data.success) {
          // Transform to match expected format
          const transformedSuppliers = data.data.map((supplier: any) => ({
            id: supplier.id,
            businessName: supplier.businessName || supplier.name,
            isActive: supplier.isActive !== false
          }));
          setSuppliers(transformedSuppliers);
        } else {
          toast.error('Error al cargar proveedores');
        }
      } catch (error) {
        console.error('Error loading suppliers:', error);
        toast.error('Error al cargar proveedores');
      } finally {
        setSuppliersLoading(false);
      }
    };

    if (selectedLocation?.id) {
      loadSuppliers();
    }
  }, [selectedLocation]);

  // Establecer valor por defecto para el selector de ubicación cuando se cargan las ubicaciones
  useEffect(() => {
    if (accessibleLocations.length > 0 && !selectedLocationId) {
      // Si hay ubicaciones disponibles y no hay una seleccionada, usar la primera
      setSelectedLocationId(accessibleLocations[0].id.toString());
    } else if (accessibleLocations.length > 0 && selectedLocationId && selectedLocationId !== 'all') {
      // Verificar que la ubicación seleccionada existe en las disponibles
      const locationExists = accessibleLocations.some(loc => loc.id.toString() === selectedLocationId);
      if (!locationExists) {
        setSelectedLocationId(accessibleLocations[0].id.toString());
      }
    }
  }, [accessibleLocations]);

  // Generar grupos por ubicación cuando se carguen los ingredientes
  useEffect(() => {
    if (ingredients.length > 0 && accessibleLocations.length > 0 && selectedLocationId && selectedLocationId !== 'all') {
      // Transform database ingredients to match expected format
      const transformedIngredients = ingredients.map(dbIngredient => ({
        id: dbIngredient.id.toString(),
        name: dbIngredient.name,
        description: dbIngredient.description,
        category: dbIngredient.category || 'Sin categoría',
        unit: dbIngredient.unit || 'unidad',
        supplier: dbIngredient.supplierId?.toString() || null, // Use supplier ID for proper lookup
        supplierName: dbIngredient.supplierName || 'Sin proveedor', // Add supplier name field
        globalCostPerUnit: parseFloat(dbIngredient.costUnit) || 0, // Use real cost from database
        costPerUnit: parseFloat(dbIngredient.costUnit) || 0, // Use real cost from database
        currentStock: parseFloat(dbIngredient.stockActual) || 0, // Use real stock from database
        minStock: dbIngredient.stockMinimo || 0, // Use real minStock from purchase_order_safety_stocks
        maxStock: 100, // Could be added to database later
        isActive: true,
        // SOLO crear locationStocks para la ubicación seleccionada en el dropdown
        locationStocks: [{
          locationId: selectedLocationId, // Usar la ubicación seleccionada en el dropdown
          currentStock: parseFloat(dbIngredient.stockActual) || 0, // Use real stock data
          minStock: dbIngredient.stockMinimo || 0, // Use real minStock from purchase_order_safety_stocks
          maxStock: 100,
          lastUpdated: new Date().toISOString()
        }]
      }));

      // Transform database locations to match expected format - SOLO la ubicación seleccionada
      const currentLocation = accessibleLocations.find(loc => loc.id.toString() === selectedLocationId);
      if (!currentLocation) {
        console.warn('No se encontró la ubicación seleccionada:', selectedLocationId);
        return;
      }

      const transformedLocations = [{
        id: currentLocation.id.toString(),
        name: currentLocation.nombre,
        address: currentLocation.direccion,
        phone: currentLocation.telefono
      }];

      // Incluir solo la ubicación seleccionada para evitar duplicados
      console.log('🔄 Generating location groups for selected location:', {
        ingredients: transformedIngredients.length,
        selectedLocationId,
        currentLocation: transformedLocations[0],
        sampleIngredient: transformedIngredients[0]
      });

      const groups = IngredientPreloadService.generateLocationGroups(
        transformedIngredients,
        transformedLocations // Solo la ubicación seleccionada
      );

      console.log('✅ Generated groups:', groups);
      setLocationGroups(groups);
    } else if (selectedLocationId === 'all' && ingredients.length > 0 && accessibleLocations.length > 0) {
      // Generar grupos para TODAS las ubicaciones
      const transformedIngredients = ingredients.map(dbIngredient => ({
        id: dbIngredient.id.toString(),
        name: dbIngredient.name,
        description: dbIngredient.description,
        category: dbIngredient.category || 'Sin categoría',
        unit: dbIngredient.unit || 'unidad',
        supplier: dbIngredient.supplierId?.toString() || null,
        supplierName: dbIngredient.supplierName || 'Sin proveedor',
        globalCostPerUnit: parseFloat(dbIngredient.costUnit) || 0,
        costPerUnit: parseFloat(dbIngredient.costUnit) || 0,
        currentStock: parseFloat(dbIngredient.stockActual) || 0,
        minStock: dbIngredient.stockMinimo || 0,
        maxStock: 100,
        isActive: true,
        locationStocks: accessibleLocations.map(loc => ({
          locationId: loc.id.toString(),
          currentStock: parseFloat(dbIngredient.stockActual) || 0,
          minStock: dbIngredient.stockMinimo || 0,
          maxStock: 100,
          lastUpdated: new Date().toISOString()
        }))
      }));

      const transformedLocations = accessibleLocations.map(loc => ({
        id: loc.id.toString(),
        name: loc.nombre,
        address: loc.direccion,
        phone: loc.telefono
      }));

      const groups = IngredientPreloadService.generateLocationGroups(
        transformedIngredients,
        transformedLocations
      );

      setLocationGroups(groups);
    }
  }, [ingredients, accessibleLocations, selectedLocationId]);

  const handleLocationGroupToggle = (locationId: string) => {
    const updatedGroups = IngredientPreloadService.updateLocationGroupExpanded(
      locationGroups,
      locationId,
      !locationGroups.find(g => g.location.id === locationId)?.isExpanded
    );
    setLocationGroups(updatedGroups);
  };

  const handleIngredientToggle = (locationId: string, ingredientId: string, isSelected: boolean) => {
    const updatedGroups = IngredientPreloadService.updateIngredientInGroup(
      locationGroups,
      locationId,
      ingredientId,
      { isSelected }
    );
    setLocationGroups(updatedGroups);
  };

  const handleQuantityChange = (locationId: string, ingredientId: string, quantity: number) => {
    if (quantity < 0) return;

    // Auto-seleccionar cuando cantidad > 0, deseleccionar cuando es 0
    const updatedGroups = IngredientPreloadService.updateIngredientInGroup(
      locationGroups,
      locationId,
      ingredientId,
      { currentQuantity: quantity, isSelected: quantity > 0 }
    );
    setLocationGroups(updatedGroups);
  };

  const handleCreateOrder = async () => {
    // Recopilar todos los ingredientes seleccionados de todos los grupos
    const allSelectedItems: Array<{
      locationId: string;
      locationName: string;
      supplierId: string;
      supplierName: string;
      ingredientId: string;
      ingredientName: string;
      quantity: number;
      unit: string;
      unitCost: number;
      notes?: string;
    }> = [];

    for (const group of locationGroups) {
      const selectedItems = group.ingredients
        .filter(item => item.isSelected && item.currentQuantity > 0)
        .map(item => ({
          locationId: group.location.id.toString(),
          locationName: group.location.name,
          supplierId: item.ingredient.supplier || '1', // Usar el supplier del ingrediente
          supplierName: item.ingredient.supplierName || 'Proveedor por defecto',
          ingredientId: item.ingredient.id.toString(),
          ingredientName: item.ingredient.name,
          quantity: item.currentQuantity,
          unit: item.ingredient.unit,
          unitCost: item.unitCost,
          notes: `${IngredientPreloadService.getReasonLabel(item.reason)} - Stock actual: ${item.ingredient.stockActual} ${item.ingredient.unit}`
        }));

      allSelectedItems.push(...selectedItems);
    }

    if (allSelectedItems.length === 0) {
      toast.error('Selecciona al menos un ingrediente');
      return;
    }

    console.log('🔧 Creating orders with items:', allSelectedItems);

    try {
      const result = await createOrdersByLocations(
        allSelectedItems,
        expectedDeliveryDate || undefined,
        notes
      );

      if (result) {
        const message = `${result.totalOrders} orden(es) de compra creada(s) exitosamente`;
        toast.success(message);

        if (result.errors && result.errors.length > 0) {
          console.warn('Errores durante la creación:', result.errors);
          toast.warning(`Algunas órdenes tuvieron errores: ${result.errors.length} errores`);
        }

        navigate('/purchase-orders');
      } else {
        toast.error('Error al crear las órdenes de compra');
      }
    } catch (error) {
      console.error('Error creating orders:', error);
      toast.error('Error al crear las órdenes de compra');
    }
  };

  const getTotalSelectedItems = () => {
    return locationGroups.reduce((total, group) => total + group.selectedCount, 0);
  };

  const getTotalSelectedValue = () => {
    return locationGroups.reduce((total, group) => total + group.totalValue, 0);
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('es-PE', {
      style: 'currency',
      currency: 'PEN'
    }).format(amount);
  };

  // Obtener ingredientes únicos de los locationGroups para el dropdown
  const getUniqueIngredientsForDropdown = () => {
    const ingredientMap = new Map();

    locationGroups.forEach(group => {
      group.ingredients.forEach(item => {
        const key = item.ingredient.id.toString();
        if (!ingredientMap.has(key)) {
          ingredientMap.set(key, {
            id: item.ingredient.id,
            name: item.ingredient.name,
            supplierId: item.supplier?.id
          });
        }
      });
    });

    return Array.from(ingredientMap.values());
  };


  if (ingredientsLoading || suppliersLoading || locationsLoading) {
    return (
      <div className="h-full flex items-center justify-center">
        <div className="text-center">
          <ShoppingCart className="w-16 h-16 text-gray-400 mx-auto mb-4 animate-pulse" />
          <h2 className="text-xl font-semibold text-gray-700">Cargando datos...</h2>
        </div>
      </div>
    );
  }

  return (
    <div className="p-3 md:p-6 space-y-4 md:space-y-6 h-full overflow-auto">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button
          variant="outline"
          onClick={() => navigate('/purchase-orders')}
          className="flex items-center gap-2"
        >
          <ArrowLeft className="w-4 h-4" />
          Volver
        </Button>
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-gray-900">Nueva Orden de Compra</h1>
          <p className="text-gray-600 mt-1 text-sm md:text-base">
            Insumos sugeridos agrupados por sucursal
          </p>
        </div>
      </div>

      {/* Configuración de la orden */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Package className="w-5 h-5" />
              Configuración de la Orden
            </div>
            <Button
              onClick={handleCreateOrder}
              disabled={getTotalSelectedItems() === 0 || creatingOrder}
              className="bg-macartur-primary hover:bg-macartur-primary/90"
            >
              <Check className="w-4 h-4 mr-2" />
              {creatingOrder ? 'Creando...' : 'Crear Orden'}
            </Button>
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Fecha de entrega esperada
              </label>
              <Input
                type="date"
                value={expectedDeliveryDate}
                onChange={(e) => setExpectedDeliveryDate(e.target.value)}
                min={new Date().toISOString().split('T')[0]}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Notas adicionales
              </label>
              <Input
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Observaciones, instrucciones especiales..."
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Filtros */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Filter className="w-5 h-5" />
            Filtros
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Filtrar por Proveedor
              </label>
              <Select
                value={selectedSupplier?.id || ''}
                onValueChange={(value) => {
                  if (value === 'all' || value === '') {
                    setSelectedSupplier(null);
                  } else {
                    const supplier = suppliers.find(s => s.id === value);
                    setSelectedSupplier(supplier || null);
                  }
                  // Reset ingredient filter when changing supplier
                  setSelectedIngredientFilter('all');
                }}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Todos los proveedores" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">
                    Todos los proveedores
                  </SelectItem>
                  {suppliers.map(supplier => (
                    <SelectItem key={supplier.id} value={supplier.id}>
                      {supplier.businessName}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Sucursal *
              </label>
              <Select
                value={selectedLocationId}
                onValueChange={setSelectedLocationId}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Seleccionar sucursal" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">
                    Todos los locales
                  </SelectItem>
                  {accessibleLocations.map(location => (
                    <SelectItem key={location.id} value={location.id.toString()}>
                      {location.nombre}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Filtrar ingredientes
              </label>
              <Select value={selectedIngredientFilter} onValueChange={setSelectedIngredientFilter}>
                <SelectTrigger>
                  <SelectValue placeholder="Mostrar todos los ingredientes" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos los ingredientes</SelectItem>
                  {getUniqueIngredientsForDropdown()
                    .filter(ingredient =>
                      !selectedSupplier ||
                      ingredient.supplierId === parseInt(selectedSupplier.id)
                    )
                    .map((ingredient) => (
                      <SelectItem key={ingredient.id} value={ingredient.id.toString()}>
                        {ingredient.name}
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>


      {/* Grupos por ubicación */}
      <div className="space-y-4">
        {locationGroups.length === 0 ? (
          <Card>
            <CardContent className="p-8 text-center">
              <Package className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-600 mb-2">
                {selectedLocationId === 'all' ? 'Selecciona una sucursal específica' : 'No hay insumos sugeridos'}
              </h3>
              <p className="text-gray-500">
                {selectedLocationId === 'all'
                  ? 'Para ver los insumos sugeridos, selecciona una sucursal específica en el filtro de arriba'
                  : selectedLocationId
                    ? 'No se encontraron insumos que requieran reposición en esta sucursal'
                    : 'Selecciona una sucursal para ver los insumos sugeridos'
                }
              </p>
            </CardContent>
          </Card>
        ) : (
          locationGroups.map(group => (
            <Card key={group.location.id}>
              <CardHeader 
                className="cursor-pointer"
                onClick={() => handleLocationGroupToggle(group.location.id)}
              >
                <CardTitle className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    {group.isExpanded ? (
                      <ChevronDown className="w-5 h-5 text-gray-500" />
                    ) : (
                      <ChevronRight className="w-5 h-5 text-gray-500" />
                    )}
                    <MapPin className="w-5 h-5 text-macartur-primary" />
                    <span>{group.location.name}</span>
                    <Badge variant="secondary">
                      {group.selectedCount} de {group.totalCount}
                    </Badge>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-lg font-bold text-green-600">
                      {formatCurrency(group.totalValue)}
                    </span>
                  </div>
                </CardTitle>
              </CardHeader>
              
              {group.isExpanded && (
                <CardContent>
                  <PurchaseOrderIngredientsTable
                    ingredients={group.ingredients}
                    suppliers={suppliers}
                    onToggle={(ingredientId, isSelected) =>
                      handleIngredientToggle(group.location.id, ingredientId, isSelected)
                    }
                    onQuantityChange={(ingredientId, quantity) =>
                      handleQuantityChange(group.location.id, ingredientId, quantity)
                    }
                    selectedIngredientFilter={selectedIngredientFilter}
                  />
                </CardContent>
              )}
            </Card>
          ))
        )}
      </div>
    </div>
  );
};


export default PurchaseOrderCreatePage;